import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Keyed on the url rather than the history entry: react-router leaves the entry key
// undefined for the first one, so every route would share a single bucket, and going
// back across two entries lands on a different entry than the one that saved.
const useScrollRestoration = ({ canRestore = true } = {}) => {
  const location = useLocation();
  const scrollKey = `scroll:${location.pathname}${location.search}`;
  const lastPositionRef = useRef(0);

  // Save scroll position before leaving
  useEffect(() => {
    lastPositionRef.current = window.scrollY;

    // Sampled while the page is still scrolled: by the time the cleanup below runs, the
    // outgoing navigation has already begun unwinding the position and window.scrollY
    // reads a partial value.
    const handleScroll = () => {
      lastPositionRef.current = window.scrollY;
    };

    const persistPosition = () => {
      sessionStorage.setItem(scrollKey, lastPositionRef.current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("beforeunload", persistPosition);

    return () => {
      persistPosition();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", persistPosition);
    };
  }, [scrollKey]);

  // Restore scroll position once the list it belongs to has something to scroll through
  useEffect(() => {
    if (!canRestore) {
      return;
    }

    const savedPosition = sessionStorage.getItem(scrollKey);

    if (savedPosition !== null) {
      window.scrollTo(0, parseInt(savedPosition, 10) || 0);
    } else {
      window.scrollTo(0, 0); // fallback
    }
  }, [scrollKey, canRestore]);
};

export default useScrollRestoration;
